import { FC, useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Popper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Close,
  DeleteOutline,
  SearchOutlined,
} from "@mui/icons-material";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import {
  TSearchProductV4,
  ProductWarehouse,
  searchProductsES,
} from "@/api";
import { country } from "@/config/country";
import useDebounce from "@/hooks/debounce";

type CartProduct = {
  id: string;
  name: string;
  sku: string;
  image?: string;
  retailPrice: number;
  wholesalePrice: number;
  warehouseId: string;
  availableWarehouses: ProductWarehouse[];
  partialUnitsAllowed?: boolean;
  quantity: number;
};

const formatPrice = (n: number) =>
  new Intl.NumberFormat(country.locale ?? "es-CO", {
    style: "currency",
    currency: country.currency ?? "COP",
    maximumFractionDigits: 0,
  }).format(n || 0);

/**
 * ProductPickerV2 — search-as-you-type product picker with a cart below.
 * Each cart row picks its own warehouse from the product's
 * availableWarehouses (mirrors the suppliers-web flow).
 */
export const ProductPickerV2: FC = () => {
  const { control } = useFormContext();
  const { fields, append, remove, update } = useFieldArray({
    control,
    name: "products",
    keyName: "_key",
  });

  const cart = (useWatch({ name: "products" }) as CartProduct[]) ?? [];
  const cartIds = useMemo(() => new Set(cart.map((p) => p?.id)), [cart]);

  return (
    <Stack spacing={2}>
      <ProductSearchInput
        excludeIds={cartIds}
        onPick={(p) => {
          const firstWh = p.availableWarehouses?.[0]?.warehouseId ?? "";
          append({
            id: p.id,
            name: p.name,
            sku: p.sku,
            image: p.media?.[0]?.src ?? p.images?.[0],
            retailPrice: p.retailPrice,
            wholesalePrice: p.wholesalePrice,
            partialUnitsAllowed: p.partialUnitsAllowed,
            warehouseId: firstWh,
            availableWarehouses: p.availableWarehouses ?? [],
            supplierId: p.supplierId,
            quantity: 1,
          } as any);
        }}
      />

      {fields.length === 0 ? (
        <Box
          sx={{
            border: 1,
            borderStyle: "dashed",
            borderColor: "divider",
            borderRadius: 2,
            p: 4,
            textAlign: "center",
            color: "text.secondary",
          }}
        >
          <Typography variant="body2">
            Buscá un producto arriba para agregarlo al pedido.
          </Typography>
        </Box>
      ) : (
        <Stack spacing={1}>
          {fields.map((field: any, idx) => (
            <CartRow
              key={field._key}
              row={cart[idx] ?? field}
              onRemove={() => remove(idx)}
              onChange={(patch) =>
                update(idx, { ...(cart[idx] ?? field), ...patch })
              }
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
};

const ProductSearchInput: FC<{
  excludeIds: Set<string>;
  onPick: (p: TSearchProductV4) => void;
}> = ({ excludeIds, onPick }) => {
  const [text, setText] = useState("");
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState<TSearchProductV4[]>([]);
  const [loading, setLoading] = useState(false);
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const debounced = useDebounce(text, 250);

  useEffect(() => {
    let cancelled = false;
    if (!debounced || debounced.length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    searchProductsES({
      text: debounced,
      countryCode: country.code,
      hasStock: true,
      status: true,
      brandId: [],
      categoryId: [],
      page: 0,
      size: 12,
      sort: { field: "name", sort: "asc" },
    })
      .then((res) => {
        if (cancelled) return;
        setResults(res.content);
      })
      .catch(() => {
        if (!cancelled) setResults([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debounced]);

  return (
    <Box ref={anchorRef} sx={{ position: "relative" }}>
      <TextField
        fullWidth
        size="medium"
        placeholder="Buscar por nombre o SKU…"
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setOpen(true);
        }}
        onFocus={() => text.length >= 2 && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchOutlined fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: loading ? (
            <InputAdornment position="end">
              <CircularProgress size={16} />
            </InputAdornment>
          ) : text ? (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={() => {
                  setText("");
                  setResults([]);
                }}
              >
                <Close fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
      />
      <Popper
        open={open && results.length > 0}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        style={{
          width: anchorRef.current?.offsetWidth,
          zIndex: 1300,
        }}
        modifiers={[{ name: "offset", options: { offset: [0, 4] } }]}
      >
        <Paper
          variant="outlined"
          sx={{
            maxHeight: 360,
            overflowY: "auto",
            borderRadius: 2,
            mt: 0.5,
          }}
        >
          {results.map((p) => {
            const disabled = excludeIds.has(p.id);
            const totalStock =
              p.availableWarehouses?.reduce(
                (acc, w) => acc + (w.availableStock ?? 0),
                0
              ) ?? 0;
            return (
              <Box
                key={p.id}
                onMouseDown={(e) => {
                  e.preventDefault();
                  if (!disabled) onPick(p);
                }}
                sx={{
                  p: 1,
                  display: "flex",
                  gap: 1.5,
                  alignItems: "center",
                  borderBottom: 1,
                  borderColor: "divider",
                  cursor: disabled ? "default" : "pointer",
                  opacity: disabled ? 0.5 : 1,
                  "&:hover": {
                    bgcolor: disabled ? undefined : "action.hover",
                  },
                  "&:last-of-type": { borderBottom: 0 },
                }}
              >
                <Avatar
                  src={p.media?.[0]?.src ?? p.images?.[0]}
                  alt={p.name}
                  variant="rounded"
                  sx={{ width: 44, height: 44, bgcolor: "action.hover" }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {p.name}
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ mt: 0.25 }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontFamily: "monospace" }}
                    >
                      {p.sku}
                    </Typography>
                    <Typography variant="caption" color="text.disabled">
                      ·
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {totalStock} en stock
                    </Typography>
                  </Stack>
                </Box>
                <Stack alignItems="flex-end" sx={{ flexShrink: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {formatPrice(p.wholesalePrice)}
                  </Typography>
                  {disabled && (
                    <Typography variant="caption" color="text.disabled">
                      ya agregado
                    </Typography>
                  )}
                </Stack>
              </Box>
            );
          })}
        </Paper>
      </Popper>
    </Box>
  );
};

const CartRow: FC<{
  row: CartProduct;
  onRemove: () => void;
  onChange: (patch: Partial<CartProduct>) => void;
}> = ({ row, onRemove, onChange }) => {
  const warehouses = row?.availableWarehouses ?? [];
  const currentWh = warehouses.find((w) => w.warehouseId === row.warehouseId);
  const stockAvailable = currentWh?.availableStock ?? 0;
  const exceeds = (row.quantity ?? 0) > stockAvailable;
  const step = row?.partialUnitsAllowed ? 0.1 : 1;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 1.25, sm: 1.5 },
        borderRadius: 2,
        borderColor: exceeds ? "error.main" : "divider",
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        alignItems={{ sm: "center" }}
      >
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ flex: 1, minWidth: 0 }}
        >
          <Avatar
            src={row.image}
            alt={row.name}
            variant="rounded"
            sx={{
              width: 48,
              height: 48,
              bgcolor: "action.hover",
              flexShrink: 0,
            }}
          />
          <Stack sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {row.name}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontFamily: "monospace" }}
              >
                {row.sku}
              </Typography>
              <Typography variant="caption" color="text.disabled">
                ·
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatPrice(row.wholesalePrice)}
              </Typography>
            </Stack>
          </Stack>
        </Stack>

        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ width: { xs: "100%", sm: "auto" } }}
        >
          <TextField
            select
            size="small"
            label="Bodega"
            value={row.warehouseId ?? ""}
            onChange={(e) => onChange({ warehouseId: e.target.value })}
            SelectProps={{
              renderValue: (val) => {
                const w = warehouses.find((x) => x.warehouseId === val);
                return (
                  <Typography
                    variant="body2"
                    sx={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {w?.warehouseName ?? "Sin bodega"}
                  </Typography>
                );
              },
            }}
            sx={{ minWidth: 180, flex: { xs: 1, sm: "0 0 auto" } }}
          >
            {warehouses.length === 0 ? (
              <MenuItem value="" disabled>
                Sin bodegas con stock
              </MenuItem>
            ) : (
              warehouses.map((w) => (
                <MenuItem
                  key={w.warehouseId}
                  value={w.warehouseId}
                  disabled={(w.availableStock ?? 0) <= 0 && !w.overStock}
                >
                  <Stack>
                    <Typography variant="body2">{w.warehouseName}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {w.availableStock} disp.
                    </Typography>
                  </Stack>
                </MenuItem>
              ))
            )}
          </TextField>

          <TextField
            size="small"
            label="Cant."
            type="number"
            value={row.quantity}
            onChange={(e) =>
              onChange({ quantity: Number(e.target.value) || 0 })
            }
            inputProps={{
              min: row?.partialUnitsAllowed ? 0.1 : 1,
              step,
              style: { textAlign: "right" },
            }}
            error={exceeds}
            helperText={exceeds ? `Máx ${stockAvailable}` : undefined}
            sx={{ width: 96 }}
          />

          <Tooltip title="Quitar">
            <IconButton onClick={onRemove} size="small">
              <DeleteOutline fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
    </Paper>
  );
};
